import {GenexSISModel} from "@/models/sis/GenexSISModel";
import CourseScope from "@/models/sis/CourseScope";
import Objective from "@/models/sis/Objective";
import Campus from "@/models/sis/Campus";
import Topic from "@/models/sis/Topic";
import TeacherReflection from "@/models/sis/TeacherReflection";
import ClassGroup from "@/models/sis/ClassGroup";
import Subject from "@/models/sis/Subject";
import User from "@/models/sis/User";
import {format} from "date-fns";
import GenexHelperMixins from "@/mixins/GenexHelperMixins";
import {normalize} from "@/utils/data";

/**
 * A Lesson Plan
 * @property {number} id
 * @property {number} course_scope_id - The id of the course-scope associated with this lesson plan
 * @property {number} course_scope_id - The id of the class associated with this lesson plan
 * @property {Object} courseScope - The courseScope this lesson plan belongs to
 * @property {number} campus_id - The id of the campus this lesson plan belongs to
 * @property {Object} campus - the campus this lesson plan belongs to
 * @property {string} starter_activity - The starter activity of the lesson
 * @property {string} engagement - The engagement of the lesson
 * @property {string} checkpoint - The checkpoint of the lesson
 * @property {string} resources - The resources string of the lesson
 * @property {Array.<number>} objective_ids - the ids of the objectives in this lesson plan
 * @property {Array.<Object>} objectives - The objectives in this lesson plan
 */
export default class LessonPlan extends GenexSISModel {
    static entity = "lessonPlans";

    get formatted_date() {
        return format(new Date(this.created_at), "yyyy-MM-dd hh:mm");
    }

    static fields() {
        return {
            id: this.attr(null),
            course_scope_id: this.attr(null),
            created_at: this.attr(null),
            class_group_id: this.attr(null),
            lesson_date: this.attr(null),
            class_group: this.belongsTo(ClassGroup, "class_group_id"),
            subject_id: this.attr(null),
            subject: this.belongsTo(Subject, "subject_id"),
            course_scope: this.belongsTo(CourseScope, "course_scope_id"),
            course_scope_topic_id: this.attr(null),
            topic: this.belongsTo(Topic, "course_scope_topic_id"),
            campus_id: this.attr(null),
            campus: this.belongsTo(Campus, "campus_id"),
            objective_ids: this.attr([]),
            objectives: this.hasManyBy(Objective, "objective_ids"),
            starter_activity: this.attr(null),
            learner_reflections_count: this.attr(null),
            teacher_reflection_count: this.attr(null),
            teacher_reflections: this.hasMany(TeacherReflection, "lesson_plan_id"),
            checkpoint: this.attr(null),
            engagement: this.attr(null),
            content: this.attr(null),
            resources: this.attr(null),
            needs_learner_reflections: this.attr(0),
            is_global: this.attr(0),
            is_lms: this.attr(0),
            time_estimate: this.attr(null),
            last_copied: this.attr(null),
            name: this.attr(null),
            user_id: this.attr(null),
            user: this.belongsTo(User, "user_id"),
        };
    }

    /**
     * Returns all Lesson Plans the user has access to
     * @function
     * @param {Object} pagination
     * @param {number} pagination.page - Which page to retrieve
     * @param {number} pagination.limit - How many entities to retrieve
     * @param {Object} query
     * @param {number} [query.campus_id]
     * @param {number} [query.course_scope_id]
     * @param {string} [query.lesson_date]
     * @param {Array.<string>} [relationships=[]] - Relationships to bring along
     * @param {Array.<string>} [count=[]] - Relationships to count
     * @returns {Promise<Response>} All the Lesson Plans
     */

    static FetchAll({page = 1, limit = 15}, query = {}, relationships = [], count = []) {
        return this.api().get(`/lesson-plans`, {
            ...GenexHelperMixins.methods.DefaultSISHeadersAndBaseUrl(),
            params: {
                ...{
                    page: page,
                    limit: limit,
                    with: relationships,
                    with_count: count,
                },
                ...(query !== {} ? query : {}),
            },
            dataTransformer: ({data: {data}}) => {
                return normalize(data);
            },
        });
    }

    /**
     * Returns an Objective
     * @function
     * @param {number} id - The id of the Objective
     * @param {Array.<string>} [relationships=[]] - Relationships to bring along
     * @returns {Promise<Response>} the Topic
     */

    static FetchById(id, relationships = []) {
        return this.api().get(`/lesson-plans/${id}`, {
            ...GenexHelperMixins.methods.DefaultSISHeadersAndBaseUrl(),
            params: {
                ...{
                    with: relationships,
                },
            },
            dataTransformer: ({data: {data}}) => {
                return normalize(data);
            },
        });
    }

    /**
     * Download lesson plan
     * @function
     * @param {number} id - The id of the Lesson Plan
     */
    static Download(id) {
        return this.api().get(`/lesson-plans/${id}/download`, {
            ...GenexHelperMixins.methods.DefaultSISHeadersAndBaseUrl(),
            responseType: "arraybuffer",
            save: false,
        });
    }
}
